class WwkHeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.mainSummary = this.querySelector('.wwk-menu-toggle');
    this.header = null;
    this.onResize = this.onResize.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    if (!this.mainDetailsToggle || !this.mainSummary) return;

    this.mainSummary.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelectorAll('.wwk-menu-drawer__details').forEach((details) => {
      details.addEventListener('toggle', this.onAppDrawerToggle.bind(this));
      const summary = details.querySelector('summary');
      summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      // A section can render pre-opened (e.g. it contains the current page).
      // Render its grid immediately so it isn't visibly empty on load.
      if (details.open) {
        this.renderPanel(details);
      }
    });

    this.addEventListener('keyup', this.onKeyUp);
  }

  // Clone the panel's <template> contents into the DOM (or reattach a
  // previously-cloned, cached node) so tile images only enter the DOM
  // — and only get fetched — once their section is actually opened.
  renderPanel(details) {
    const panel = details.querySelector('.wwk-menu-drawer__panel');
    if (!panel || panel.dataset.rendered === 'true') return;

    if (!panel._wwkGrid) {
      const template = panel.querySelector('template.wwk-menu-template');
      if (!template) return;
      panel._wwkGrid = template.content.firstElementChild.cloneNode(true);

      // Fade each tile image in as it actually finishes loading, rather
      // than all-at-once — avoids the flicker of images popping in raw
      // at whatever time their individual request happens to resolve.
      panel._wwkGrid.querySelectorAll('img').forEach((img) => {
        if (img.complete) {
          img.classList.add('wwk-is-loaded');
        } else {
          img.addEventListener('load', () => img.classList.add('wwk-is-loaded'), { once: true });
        }
      });
    }

    panel.appendChild(panel._wwkGrid);
    panel.dataset.rendered = 'true';
  }

  // Detach (but don't discard) the rendered grid so its images stop
  // costing memory/decoding while the section is collapsed. The node is
  // cached on the panel so reopening is instant and triggers no re-fetch.
  collapseDrawer(details) {
    details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');

    const panel = details.querySelector('.wwk-menu-drawer__panel');
    if (!panel) return;

    panel._wwkGrid?.remove();
    delete panel.dataset.rendered;
  }

  onSummaryClick(event) {
    if (this.mainDetailsToggle.open) {
      event.preventDefault();
      this.close(this.mainSummary);
      return;
    }

    this.open();
  }

  open() {
    this.header = this.header || document.querySelector('.section-header');
    const headerBottom = this.header ? parseInt(this.header.getBoundingClientRect().bottom, 10) : 0;

    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    this.header?.classList.add('menu-open');

    window.setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    this.mainSummary.setAttribute('aria-expanded', 'true');
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint || 'tablet'}`);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onResize);
  }

  close(elementToFocus) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainSummary.setAttribute('aria-expanded', 'false');
    this.header?.classList.remove('menu-open');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint || 'tablet'}`);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);

    window.setTimeout(() => {
      this.mainDetailsToggle.removeAttribute('open');
      elementToFocus?.focus();
    }, 260);
  }

  onAppDrawerToggle(event) {
    const activeDetails = event.currentTarget;
    const summary = activeDetails.querySelector('summary');
    summary?.setAttribute('aria-expanded', activeDetails.open ? 'true' : 'false');

    if (!activeDetails.open) {
      // Collapsed via native <details> behavior (e.g. clicking its own
      // summary again) — still tear down its rendered grid.
      const panel = activeDetails.querySelector('.wwk-menu-drawer__panel');
      panel?._wwkGrid?.remove();
      if (panel) delete panel.dataset.rendered;
      return;
    }

    this.querySelectorAll('.wwk-menu-drawer__details[open]').forEach((details) => {
      if (details === activeDetails) return;
      this.collapseDrawer(details);
    });

    this.renderPanel(activeDetails);
  }

  onDocumentClick(event) {
    if (!this.mainDetailsToggle.open || this.contains(event.target)) return;
    this.close(this.mainSummary);
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.mainDetailsToggle.open) return;
    this.close(this.mainSummary);
  }

  onResize() {
    const headerBottom = this.header ? parseInt(this.header.getBoundingClientRect().bottom, 10) : 0;
    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }
}

customElements.define('wwk-header-drawer', WwkHeaderDrawer);
